import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { getAccessToken } from "./auth";

const DEBUG_API = import.meta.env.DEV && import.meta.env.VITE_DEBUG_API === "true";

// API error response type
export interface IApiError {
  code: string;
  message: string;
  details?: Array<{
    field: string;
    message: string;
  }>;
}

export interface IApiErrorResponse {
  error: IApiError;
  meta: {
    timestamp: string;
    requestId: string;
  };
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getAccessToken();
    // NOTE: When hosted behind Azure Static Web Apps, the platform may overwrite the
    // `Authorization` header when proxying requests to Azure Functions.
    // We send the Entra access token in a dedicated header so the API can validate
    // the real client token reliably.
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      config.headers["X-Entra-Access-Token"] = token;
      if (DEBUG_API) {
        console.warn("[API Client] Attached auth token for request:", config.url);
      }
    } else if (DEBUG_API) {
      console.warn("[API Client] No token available for request:", config.url);
    }

    // Forward simulated role to API (admin feature)
    try {
      const simRole = localStorage.getItem("ai-registry-role-simulator");
      if (simRole) {
        config.headers["X-Simulated-Role"] = simRole;
      }
    } catch {
      // Ignore localStorage errors
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<IApiErrorResponse>) => {
    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Token expired or invalid - log but don't auto-redirect
          // Components should handle 401 errors and redirect to login if needed
          if (DEBUG_API && !window.location.pathname.includes("/login")) {
            console.warn(
              "[API Client] 401 Unauthorized - Token may be expired or invalid"
            );
            console.warn("[API Client] Request URL:", error.config?.url);
            console.warn("[API Client] Current path:", window.location.pathname);
            // Don't auto-redirect - let the component handle it
            // This prevents interrupting the render cycle and avoids redirect loops
          }
          break;
        case 403: {
          // Forbidden - user doesn't have permission
          const message = 'error' in data && typeof data.error.message === 'string'
            ? data.error.message
            : "You don't have permission to access this resource";
          console.error("Access forbidden:", message);
          break;
        }
        case 404:
          // Not found - let the component handle this gracefully
          if (DEBUG_API) {
            console.warn("Resource not found:", error.config?.url);
          }
          break;
        case 429:
          // Rate limited
          console.error("Rate limit exceeded. Please try again later.");
          break;
        case 500:
        case 503:
          // Server error
          console.error("Server error. Please try again later.");
          break;
      }
    } else if (error.request) {
      // Network error
      if (DEBUG_API) {
        console.error("Network error. Please check your connection.");
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient };

/** True when an error is an HTTP 403 permission failure (401 = session expiry, handled separately) */
export function isAccessError(error: unknown): boolean {
  if (typeof error === "object" && error !== null && "response" in error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    return status === 403;
  }
  return false;
}

// Helper function to extract error message
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<IApiErrorResponse>;
    if (axiosError.response?.data) {
      return axiosError.response.data.error.message;
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}

// Helper function to extract validation errors
export function getValidationErrors(
  error: unknown
): Record<string, string> | null {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<IApiErrorResponse>;
    if (axiosError.response) {
      const details = axiosError.response.data.error.details;
      if (details && details.length > 0) {
        return details.reduce<Record<string, string>>((acc, detail) => {
          acc[detail.field] = detail.message;
          return acc;
        }, {});
      }
    }
  }
  return null;
}
